/** The Documents page's package indexes and the sweep status for a tag in context. */
import type { PackageScanStatus } from "../lib/api";
import { ApiError, apiGet, apiSend } from "../lib/http";

export type IndexKind = "notices" | "visits";

/** One index run started from this screen. An earlier one is read from `fetchIndexes`. */
export type IndexRun =
  | { status: "idle" }
  | { status: "running" }
  | { status: "done"; documents: number }
  | { status: "failed"; error: string };

/**
 * Index the manufacturer notices or the site visit reports of a package. Notices answer when done,
 * in seconds. Visit reports take minutes of local OCR, so the server queues them and answers at
 * once; the page then reads their progress from `fetchIndexes` like any reload would.
 */
export async function indexPackage(kind: IndexKind, pkg: string): Promise<IndexRun> {
  try {
    if (kind === "visits") {
      // Queued on the server and read back from `fetchIndexes`, so a reload sees it running.
      await apiSend("/api/v1/uc1/packages/{package}/visits", undefined, { path: { package: pkg } });
      return { status: "running" };
    }
    const out = await apiSend("/api/v1/uc1/packages/{package}/notices", undefined, { path: { package: pkg } });
    return { status: "done", documents: Number((out as { documents?: unknown }).documents ?? 0) };
  } catch (e) {
    return { status: "failed", error: (e as Error).message };
  }
}

/** The Documents screen for one open package. */
export function documentsPath(pkg: string): string {
  return `/documents/${encodeURIComponent(pkg)}`;
}

/**
 * An index run as the server reports it. `running` and `failed` come from the work queue, `done`
 * from the audit trail; a run without `status` is from an API before it was reported (done).
 */
export interface LastIndexRun {
  status?: "running" | "done" | "failed";
  at: string;
  by: string;
  documents?: number | null;
  error?: string | null;
  last_done?: LastIndexRun | null;
}

/** Whether the server says an index of this package is still running, so the page should poll. */
export function serverRunning(status: Pick<PackageIndexStatus, IndexKind> | null): boolean {
  return !!status && (["notices", "visits"] as const).some((k) => status[k]?.status === "running");
}
export interface PackageIndexStatus { package: string; notices: LastIndexRun | null; visits: LastIndexRun | null }

/** The last notices and visit reports index run on a package, so the card survives a reload. */
export async function fetchIndexes(pkg: string): Promise<PackageIndexStatus> {
  return (await apiGet("/api/v1/uc1/packages/{package}/indexes", { path: { package: pkg } })) as unknown as PackageIndexStatus;
}

export type ScanStatus = PackageScanStatus & { tag_documents?: Record<string, number[]> | null };

/**
 * The package's content sweep, and with a tag the documents and pages the sweep read it on,
 * matched the way estate search matches it. Null when no sweep has been built.
 */
export async function scanStatusFor(pkg: string, tag: string | null): Promise<ScanStatus | null> {
  try {
    const out = await apiGet("/api/v1/uc1/packages/{package}/scan", {
      path: { package: pkg }, query: tag ? { tag } : {},
    });
    return out as unknown as ScanStatus;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}
